import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@skybeam/shared-utils';
import { LoaderCircle } from 'lucide-react';
import { focusVisibleClasses } from '../../utils/focus-styles';

export const BUTTON_VARIANTS = {
  primary: 'primary',
  secondary: 'secondary',
  destructive: 'destructive',
  tertiary: 'tertiary',
  link: 'link',
  default: 'default',
  outline: 'outline',
  ghost: 'ghost',
} as const;

export const BUTTON_SIZES = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  default: 'default',
  icon: 'icon',
} as const;

export const BUTTON_THEMES = {
  light: 'light',
  dark: 'dark',
  auto: 'auto',
} as const;

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium disabled:pointer-events-none disabled:opacity-50 font-semibold',
  ),
  {
    variants: {
      variant: {
        [BUTTON_VARIANTS.primary]: cn(
          'bg-button-primary-active hover:bg-button-primary-hover active:bg-button-primary-pressed disabled:bg-button-primary-disabled text-white dark:text-black focus-visible:bg-button-primary-hover',
          focusVisibleClasses.primary,
        ),
        [BUTTON_VARIANTS.secondary]: cn(
          'bg-button-secondary-active hover:bg-button-secondary-hover active:bg-button-secondary-pressed disabled:bg-button-secondary-disabled text-text-active focus-visible:bg-button-secondary-hover',
          focusVisibleClasses.primary,
        ),
        [BUTTON_VARIANTS.destructive]: cn(
          'bg-button-destructive-active hover:bg-button-destructive-hover active:bg-button-destructive-pressed disabled:bg-button-destructive-disabled text-white focus-visible:bg-button-destructive-hover',
          focusVisibleClasses.destructive,
        ),
        [BUTTON_VARIANTS.tertiary]: cn(
          'bg-button-tertiary-active hover:bg-button-tertiary-hover active:bg-button-tertiary-pressed disabled:bg-button-tertiary-disabled text-text-control focus-visible:bg-button-tertiary-hover',
          focusVisibleClasses.primary,
        ),
        [BUTTON_VARIANTS.link]:
          'text-primary underline-offset-2 hover:underline hover:text-button-link-hover active:text-button-link-active active:underline font-base font-normal p-0 focus-visible:outline-none',
        [BUTTON_VARIANTS.default]:
          'bg-button-primary-active hover:bg-button-primary-hover active:bg-button-primary-pressed disabled:bg-button-primary-disabled text-white',
        [BUTTON_VARIANTS.outline]: cn(
          'border border-input-border bg-button-outline-active hover:bg-button-outline-hover active:bg-button-outline-pressed disabled:bg-button-outline-disabled focus-visible:bg-button-outline-hover',
          focusVisibleClasses.primary,
        ),
        [BUTTON_VARIANTS.ghost]: cn(
          'hover:bg-button-ghost-hover active:bg-button-ghost-pressed disabled:bg-button-ghost-disabled bg-button-ghost-active focus-visible:bg-button-ghost-hover',
          focusVisibleClasses.primary,
        ),
      },
      theme: {
        [BUTTON_THEMES.light]: 'theme-light',
        [BUTTON_THEMES.dark]: 'theme-dark',
        [BUTTON_THEMES.auto]: '',
      },
      icon: {
        false: null,
        true: 'aspect-square',
      },
      size: {
        [BUTTON_SIZES.default]: 'h-10 px-4 py-2',
        [BUTTON_SIZES.sm]: 'rounded-md',
        [BUTTON_SIZES.md]: 'rounded-md text-sm',
        [BUTTON_SIZES.lg]: 'rounded-md text-base',
        [BUTTON_SIZES.icon]: 'h-10 w-10',
      },
    },
    compoundVariants: [
      {
        size: BUTTON_SIZES.sm,
        variant: [
          BUTTON_VARIANTS.primary,
          BUTTON_VARIANTS.secondary,
          BUTTON_VARIANTS.destructive,
          BUTTON_VARIANTS.tertiary,
        ],
        className: 'h-8 px-2.5',
      },
      {
        size: BUTTON_SIZES.md,
        variant: [
          BUTTON_VARIANTS.primary,
          BUTTON_VARIANTS.secondary,
          BUTTON_VARIANTS.destructive,
          BUTTON_VARIANTS.tertiary,
        ],
        className: 'h-9 px-3.5',
      },
      {
        size: BUTTON_SIZES.lg,
        variant: [
          BUTTON_VARIANTS.primary,
          BUTTON_VARIANTS.secondary,
          BUTTON_VARIANTS.destructive,
          BUTTON_VARIANTS.tertiary,
        ],
        className: 'h-11 px-5',
      },
      { icon: true, size: BUTTON_SIZES.sm, className: 'h-8 w-8 p-1.5' },
      { icon: true, size: BUTTON_SIZES.md, className: 'h-9 w-9 p-2' },
      { icon: true, size: BUTTON_SIZES.lg, className: 'h-10 w-10 p-2.5' },
    ],
    defaultVariants: {
      variant: BUTTON_VARIANTS.default,
      size: BUTTON_SIZES.default,
      theme: BUTTON_THEMES.auto,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  themeOverride?: keyof typeof BUTTON_THEMES;
}

interface ButtonComponent
  extends React.ForwardRefExoticComponent<
    ButtonProps & React.RefAttributes<HTMLButtonElement>
  > {
  variants: typeof BUTTON_VARIANTS;
  sizes: typeof BUTTON_SIZES;
  themes: typeof BUTTON_THEMES;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      icon,
      variant = BUTTON_VARIANTS.default,
      size = BUTTON_SIZES.default,
      theme = BUTTON_THEMES.auto,
      asChild = false,
      loading = false,
      themeOverride,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    const finalTheme = themeOverride || theme;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className, icon, theme: finalTheme }),
          'gap-2',
        )}
        ref={ref}
        disabled={loading}
        type="button"
        {...props}
      >
        <Slottable>{children}</Slottable>
        {loading && <LoaderCircle className="size-4 animate-spin" />}
      </Comp>
    );
  },
) as ButtonComponent;

Button.displayName = 'Button';
Button.variants = BUTTON_VARIANTS;
Button.sizes = BUTTON_SIZES;
Button.themes = BUTTON_THEMES;

export { Button, buttonVariants };
